import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/Form/Button';
import CopyButton from 'common/components/CopyButton';
import Pagination from 'modules/settings/components/DataExport/Pagination';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import ConfirmationModal from 'common/components/Modal/ConfirmationModal';
import { Text } from 'common/styledComponents/typography';
import { useShowToast } from 'common/hooks/notification';
import {
  useDataExportTokens,
  useForgetDataExportCredentials,
  useIssueDataExportToken,
  useRevokeDataExportToken,
} from 'common/queries/dataExport';
import { DataExportToken, IssueDataExportTokenPayload, IssuedDataExportToken } from 'common/types/dataExport';
import DownloadApiGuide from 'modules/settings/components/DataExport/DownloadApiGuide';
import IssueTokenModal from 'modules/settings/components/DataExport/IssueTokenModal';
import OnceOnlySecretModal from 'modules/settings/components/DataExport/OnceOnlySecretModal';
import { SectionEmpty, SectionError, SectionLoading } from 'modules/settings/components/DataExport/SectionStatus';
import {
  errorToast,
  formatExportDate,
  formatExportDateTime,
  formatExportTime,
  tokenLabel,
} from 'modules/settings/components/DataExport/utils';
import {
  ActionsCell,
  Badge,
  Cell,
  ColumnLabel,
  Code,
  Description,
  EndpointLabel,
  EndpointValue,
  MutedLine,
  EndpointRow,
  GuideSlot,
  TokenName,
  List,
  Row,
  Section,
  SectionHeader,
  SectionTitle,
} from 'modules/settings/components/DataExport/styled';

export const TOKENS_PAGE_SIZE = 5;
const COLUMNS = '2fr 1fr 1.6fr 1.2fr 1.4fr 80px';

const DownloadTokensSection: React.FC = () => {
  const { t } = useTranslation();
  const showToast = useShowToast();
  const { data, isLoading, isError, error, refetch } = useDataExportTokens();
  // Owned here rather than by the name modal, which unmounts as soon as the token is issued.
  const issueToken = useIssueDataExportToken();
  const forgetCredentials = useForgetDataExportCredentials();
  const revokeToken = useRevokeDataExportToken();
  const [isIssueOpen, setIsIssueOpen] = React.useState(false);
  const [issuedToken, setIssuedToken] = React.useState<Pick<IssuedDataExportToken, 'token' | 'name'> | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = React.useState<DataExportToken | null>(null);

  const [page, setPage] = React.useState(0);

  const tokens = data?.tokens ?? [];
  // Revoking or issuing can shrink or grow the list: never stay on a page that no longer exists.
  const lastPage = Math.max(0, Math.ceil(tokens.length / TOKENS_PAGE_SIZE) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageTokens = tokens.slice(currentPage * TOKENS_PAGE_SIZE, (currentPage + 1) * TOKENS_PAGE_SIZE);

  const handleIssue = async (payload: IssueDataExportTokenPayload) => {
    const { token, name } = await issueToken.mutateAsync(payload);
    setIsIssueOpen(false);
    setIssuedToken({ token, name });
    // The token now lives only in this component's state: drop it from React Query.
    issueToken.reset();
    forgetCredentials();
  };

  const handleIssueClose = () => {
    setIsIssueOpen(false);
    issueToken.reset();
    forgetCredentials();
  };

  const handleIssuedTokenClose = () => {
    setIssuedToken(null);
  };

  const handleRevoke = () => {
    if (!tokenToRevoke) return;
    const { id } = tokenToRevoke;
    const label = tokenLabel(tokenToRevoke);

    revokeToken.mutate(id, {
      onSuccess: () => showToast({ status: 'success', message: t('dataExport.tokens.revoked', { token: label }) }),
      onError: e => showToast(errorToast(e, t('dataExport.tokens.revokeFailed'))),
      onSettled: () => setTokenToRevoke(null),
    });
  };

  const renderList = () => {
    if (isLoading) return <SectionLoading />;
    if (isError) return <SectionError error={error} onRetry={() => refetch()} />;
    if (!tokens.length) {
      return (
        <SectionEmpty
          icon='lock'
          title={t('dataExport.tokens.emptyTitle')}
          message={t('dataExport.tokens.empty')}
          action={{ label: t('dataExport.tokens.issueFirst'), onClick: () => setIsIssueOpen(true) }}
        />
      );
    }

    return (
      <List data-testid='data-export-tokens'>
        <Row columns={COLUMNS} data-header='true' aria-hidden='true'>
          {['token', 'status', 'created', 'expires', 'lastUsed'].map(column => (
            <Cell key={column}>
              <Text variant='body2' fontWeight='600'>
                {t(`dataExport.tokens.columns.${column}`)}
              </Text>
            </Cell>
          ))}
          <Cell />
        </Row>
        {pageTokens.map(token => (
          <Row key={token.id} columns={COLUMNS} data-testid='data-export-token-row'>
            <Cell>
              <ColumnLabel>{`${t('dataExport.tokens.columns.token')}: `}</ColumnLabel>
              <TokenName data-unnamed={!token.name}>{token.name || t('dataExport.tokens.unnamed')}</TokenName>
              <Code>…{token.hint}</Code>
            </Cell>
            <Cell>
              <ColumnLabel>{`${t('dataExport.tokens.columns.status')}: `}</ColumnLabel>
              <Badge state={token.state} title={token.revoked_at ? formatExportDateTime(token.revoked_at) : undefined}>
                {t(`dataExport.tokens.states.${token.state}`)}
              </Badge>
            </Cell>
            <Cell>
              <ColumnLabel>{`${t('dataExport.tokens.columns.created')}: `}</ColumnLabel>
              <Text variant='body2'>{formatExportDate(token.created_at)}</Text>
              {token.created_by && <MutedLine>{t('dataExport.by', { name: token.created_by })}</MutedLine>}
            </Cell>
            <Cell>
              <ColumnLabel>{`${t('dataExport.tokens.columns.expires')}: `}</ColumnLabel>
              <Text variant='body2'>{formatExportDate(token.expires_at)}</Text>
            </Cell>
            <Cell>
              <ColumnLabel>{`${t('dataExport.tokens.columns.lastUsed')}: `}</ColumnLabel>
              {token.last_used_at ? (
                <>
                  <Text variant='body2'>{formatExportDate(token.last_used_at)}</Text>
                  <MutedLine>{formatExportTime(token.last_used_at)}</MutedLine>
                </>
              ) : (
                <Text variant='body2' color='black50'>
                  {t('dataExport.never')}
                </Text>
              )}
            </Cell>
            <ActionsCell>
              {token.state === 'live' && (
                <SecondaryButton type='button' color='white' compact danger isButtonV2 onClick={() => setTokenToRevoke(token)}>
                  {t('dataExport.tokens.revoke')}
                </SecondaryButton>
              )}
            </ActionsCell>
          </Row>
        ))}
      </List>
    );
  };

  const renderPagination = () => <Pagination page={currentPage} pageSize={TOKENS_PAGE_SIZE} total={tokens.length} onChange={setPage} />;

  return (
    <Section aria-labelledby='data-export-tokens-title'>
      <SectionHeader>
        <SectionTitle id='data-export-tokens-title'>{t('dataExport.tokens.title')}</SectionTitle>
        <Button type='button' color='lightPurple' pill={false} isButtonV2 disabled={!data} onClick={() => setIsIssueOpen(true)}>
          {t('dataExport.tokens.issue')}
        </Button>
      </SectionHeader>
      <Description>
        <Text variant='body2' color='black75'>
          {t('dataExport.tokens.description')}
        </Text>
      </Description>
      {data?.download_api_url && (
        <EndpointRow>
          <EndpointLabel>{t('dataExport.endpoint')}</EndpointLabel>
          <EndpointValue>{data.download_api_url}</EndpointValue>
          <CopyButton value={data.download_api_url} compact={false} />
          <GuideSlot>
            <DownloadApiGuide downloadApiUrl={data.download_api_url} />
          </GuideSlot>
        </EndpointRow>
      )}
      {renderList()}
      {renderPagination()}
      <ConfirmationModal
        appearance='card'
        id='data-export-revoke-token-modal'
        open={!!tokenToRevoke}
        isLoading={revokeToken.isLoading}
        title={t('dataExport.tokens.revokeModal.title')}
        confirmButtonTitle={t('dataExport.tokens.revokeModal.confirm')}
        onCancel={() => setTokenToRevoke(null)}
        onConfirm={handleRevoke}
      >
        <Text variant='body2' color='black'>
          {t('dataExport.tokens.revokeModal.body', { token: tokenToRevoke ? tokenLabel(tokenToRevoke) : '' })}
        </Text>
      </ConfirmationModal>
      {isIssueOpen && <IssueTokenModal isSubmitting={issueToken.isLoading} onSubmit={handleIssue} onClose={handleIssueClose} />}
      {issuedToken && (
        <OnceOnlySecretModal
          modalId='data-export-issued-token-modal'
          title={t('dataExport.tokens.issuedModal.title')}
          value={issuedToken.token}
          details={
            issuedToken.name
              ? t('dataExport.tokens.issuedModal.name', { name: issuedToken.name })
              : t('dataExport.tokens.issuedModal.unnamed')
          }
          warning={t('dataExport.tokens.issuedModal.warning')}
          acknowledgeLabel={t('dataExport.tokens.issuedModal.acknowledge')}
          onClose={handleIssuedTokenClose}
        />
      )}
    </Section>
  );
};

export default DownloadTokensSection;
